import React, { useContext, useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  PageContent,
  PageSection,
  PageHeader,
  ControlContainerRow,
  ControlContainer,
} from 'common/styles/page';
import AnalyticsPillarsBar from './AnalyticsPillarsBar';
import AnalyticsPillarsTable from './AnalyticsPillarsTable';
import ToggleControl from '../../../common/components/ToggleChartControl';
import AnalyticsQuestions from './AnalyticsPillarsQuestions';
import { getCurrentOrganizationId } from '../../../common/selectors/auth';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';
import { useSelector } from 'react-redux';
import TopBarControls from 'common/layouts/AuthLayout/TopBarAssessmentPeriod';
import { usePageCopilotPrompts } from 'areas/copilot/components/CopilotPromptsContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

const AnalyticsPillars = () => {
  const organizationId = useSelector(getCurrentOrganizationId);
  const { setTopBarContent } = useContext(TopBarContext);
  const { setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro } = usePageCopilotPrompts();

  useEffect(() => {
    setTopBarContent(<TopBarControls />);
    return () => setTopBarContent(null);
  }, [setTopBarContent, organizationId]);

  useEffect(() => {
    setPagePrompts([
      {
        title: 'Pillar Questions Overview',
        displayText: 'Explain the pillar questions page and what it reveals about culture pillars.',
        prompt:
          'Review the Pillar Questions page and explain what the pillar scores and question breakdown reveal about the organization’s culture. Highlight which pillars and questions are strongest, which are weaker, and what leaders should focus on first.',
        description:
          'Use this prompt to get a concise overview of the pillar questions page and its culture implications.',
      },
      {
        title: 'Pillar Performance Summary',
        displayText: 'Summarize the performance of key pillars and questions.',
        prompt:
          'Summarize the performance of the key cultural pillars on this page. Identify the highest- and lowest-performing pillars, and explain what the question-level details suggest about the underlying strengths and weaknesses.',
        description:
          'Ask Copilot to summarize pillar performance and question-level insights.',
      },
      {
        title: 'Leadership Actions for Pillars',
        displayText: 'Recommend next actions from the pillar analysis.',
        prompt:
          'Based on the pillar and question data, recommend specific leadership actions to improve the areas with low scores and sustain the areas with high scores. Focus on practical, data-informed next steps.',
        description:
          'Use this prompt to generate leadership action recommendations from pillar analytics.',
      },
    ]);

    setPageInfoTitle('About Pillar Questions');
    setPageInfoIntro(
      'This page shows cultural pillar scores and question-level details across the organization. It helps leaders understand which aspects of culture are strong, which need work, and where to focus follow-up conversations.',
    );
    setPageInfoItems([
      {
        term: 'Pillar performance',
        description: 'Understand which pillars are driving the culture score and which need improvement.',
      },
      {
        term: 'Question breakdown',
        description: 'Use question-level details to see the specific behaviors and experiences behind the scores.',
      },
      {
        term: 'Follow-up actions',
        description: 'Turn the pillar analysis into concrete next steps for leaders and teams.',
      },
    ]);

    return () => {
      setPagePrompts([]);
      setPageInfoItems([]);
      setPageInfoTitle(undefined);
      setPageInfoIntro(undefined);
    };
  }, [setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro]);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedPillar, setSelectedPillar] = useState<number | null>(0);
  const [chartType, setChartType] = useState('bar');

  return (
    <PageContent>
      <PageSection padright>
        <PageHeader
          title="Pillar Questions"
          caption='Cultural pillar scores and question breakdown by demographic'
          controls={
            <ControlContainerRow>
              <ControlContainer justify="left">
                <ToggleControl
                  buttonLabels={[
                    { type: 'bar', icon: 'chart bar' },
                    { type: 'list', icon: 'sliders horizontal' },
                  ]}
                  setChartType={setChartType}
                />
              </ControlContainer>
            </ControlContainerRow>
          }
        />
      </PageSection>
      {chartType === 'bar' ? (
        <PageSection padright>
          <PageSection padbottom>
            <AnalyticsPillarsBar
              setSelectedCategory={setSelectedCategory}
              setSelectedPillar={setSelectedPillar}
              selectedPillar={selectedPillar}
              selectedCategory={selectedCategory}
            />
          </PageSection>
          <PageSection>
            <AnalyticsPillarsTable
              selectedPillar={selectedPillar}
              selectedCategory={selectedCategory}
            />
          </PageSection>
        </PageSection>
      ) : (
        <AnalyticsQuestions />
      )}
    </PageContent>
  );
};

export default AnalyticsPillars;
